import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import TransferMoralePaymentSection from '../../GestionTransferts/Reservations/TransferMoralePaymentSection'
import TransferPaymentPicker from '../../GestionTransferts/Reservations/TransferPaymentPicker'
import {
  isAffilieUser,
  isPartnerOrAffilieUser,
  resolveConnectedPartnerId,
} from '../../GestionTransferts/Reservations/transferBillingIds'
import { CARD_TYPE, formatDateFR, formatTND } from '../shared'
import { isAdminUser } from './buildCaReservationPayload'
import '../../shared/tamarraParity/tamarraParity.css'

/**
 * Payment step — same Tamarra layout as circuits (intro + recap + payment tiles).
 */
const TamarraCaPaymentStep = ({
  carte,
  draft,
  date,
  pax,
  total,
  errors = {},
  setField,
  stepLabel,
  title,
  hint,
}) => {
  const { t } = useTranslation()
  const admin = isAdminUser()
  const partnerB2b = isPartnerOrAffilieUser()
  const hideAffiliePaymentUi = isAffilieUser()

  const useMoraleHotelPayment =
    (admin && !partnerB2b && draft?.billing_client_kind === 'morale') || partnerB2b

  const paymentDraft = useMemo(
    () => ({
      ...draft,
      amount: total,
      date_pickup: date ? `${date} 00:00:00` : null,
    }),
    [draft, total, date],
  )

  const partnerIdForMoralePayment = partnerB2b
    ? resolveConnectedPartnerId() ?? draft?.billing_partner_id
    : draft?.billing_partner_id

  const recapLines = useMemo(() => {
    const lines = []
    if (carte?.label) {
      lines.push([
        carte.type === CARD_TYPE.BIEN_ETRE
          ? t('tamarraBooking.done.spa', { defaultValue: 'Soin' })
          : t('tamarraBooking.done.carte', { defaultValue: 'Carte' }),
        carte.label,
      ])
    }
    if (carte?.facility_label) {
      lines.push([t('cartesAcces.recherche.modal.facility'), carte.facility_label])
    }
    if (date) {
      lines.push([t('cartesAcces.recherche.modal.accessDate'), formatDateFR(date)])
    }
    if (pax != null) {
      lines.push([t('cartesAcces.recherche.modal.personsShort'), String(pax)])
    }
    return lines
  }, [carte, date, pax, t])

  return (
    <div className="tamarra-pay-page">
      <div className="tamarra-sec-intro">
        <span className="tamarra-label">
          {stepLabel ||
            t('tamarraBooking.payment.stepLabel', {
              defaultValue: 'Étape 4 sur 4 · Paiement',
            })}
        </span>
        <h3>
          {title ||
            t('tamarraBooking.payment.title', {
              defaultValue: 'Réglez votre réservation',
            })}
        </h3>
        <p>
          {hint ||
            t('tamarraBooking.payment.payHint', {
              defaultValue:
                'Choisissez le mode de paiement pour confirmer la réservation.',
            })}
        </p>
      </div>

      <div className="tamarra-recap">
        {recapLines.map(([label, value]) => (
          <div className="tamarra-r" key={`${label}-${value}`}>
            <span>{label}</span>
            <span>{value}</span>
          </div>
        ))}
        <div className="tamarra-r tamarra-rt tamarra-rbig">
          <span>{t('cartesAcces.recherche.modal.total')}</span>
          <span>{formatTND(total)}</span>
        </div>
      </div>

      {useMoraleHotelPayment ? (
        <TransferMoralePaymentSection
          draft={paymentDraft}
          setField={setField}
          partnerIdOverride={partnerIdForMoralePayment}
          hideUi={hideAffiliePaymentUi}
          appearance="tamarra"
        />
      ) : (
        <TransferPaymentPicker
          key={`ca-pay-${draft?.billing_client_id ?? 'n'}-${draft?.billing_client_kind ?? 'n'}-${draft?.billing_partner_id ?? 'np'}`}
          totalAmount={Number(total) || 0}
          value={draft?.payment_bundle}
          onChange={(v) => {
            setField('payment_bundle', v)
            setField('payment_methods', {})
          }}
          billingClientId={draft?.billing_client_id}
          billingClientKind={draft?.billing_client_kind}
          billingPartnerId={draft?.billing_partner_id}
          checkInDate={date || null}
          variant="tamarra"
        />
      )}

      {errors.payment_methods && !hideAffiliePaymentUi ? (
        <p className="tamarra-ferr">{errors.payment_methods}</p>
      ) : null}
      {errors.payment_methods && useMoraleHotelPayment && hideAffiliePaymentUi ? (
        <p className="tamarra-bk-rate">{errors.payment_methods}</p>
      ) : null}
    </div>
  )
}

export default TamarraCaPaymentStep
